import type { ComponentType } from "react";
import type { Schema } from "@/shared/components/Form/types";
import type {
  CreateSchemaDefinition,
  EditSchemaDefinition,
} from "@/shared/components/CRUD/types";
import {
  CreateMailerConfigMutation,
  UpdateMailerConfigMutation,
  MailerConfigEditQuery,
  MailerConfigShowQuery,
  type UpdateMailerConfigInput,
} from "@/admin/entities/mailerConfigs";
import type {
  MailerConfigEditQueryType,
  MailerConfigShowQueryType,
} from "@/types/graphql/admin.generated";

export const filterSchema: Schema = {
  withDeleted: {
    label: "Показывать удалённые",
    type: "checkbox",
    priority: "secondary",
    xs: 12,
    md: 6,
    fullWidth: true,
  },
  deleted: {
    label: "Статус",
    type: "select",
    priority: "secondary",
    xs: 12,
    md: 6,
    fullWidth: true,
    options: [
      { label: "Все активные и удалённые", value: "" },
      { label: "Только активные", value: "false" },
      { label: "Только удалённые", value: "true" },
    ],
  },
};

const fromAndTitleSchema: Schema = {
  from: {
    required: true,
    label: "Отправитель (From)",
    helperText: "Пример: no-reply@example.com",
    type: "email",
    xs: 12,
    fullWidth: true,
  },
  title: {
    required: true,
    label: "Название",
    helperText: "Краткое имя в списке и в карточке.",
    type: "text",
    xs: 12,
    fullWidth: true,
  },
};

const apiKeyFieldCreate: Schema = {
  apiKey: {
    required: true,
    label: "API-ключ UniOne",
    helperText:
      "Ключ из личного кабинета UniOne (не показывается после сохранения).",
    type: "password",
    xs: 12,
    fullWidth: true,
  },
};

const apiKeyFieldEdit: Schema = {
  apiKey: {
    label: "Новый API-ключ UniOne",
    helperText: "Оставьте пустым, чтобы не менять ключ.",
    type: "password",
    xs: 12,
    fullWidth: true,
  },
};

/** Ряд как siteUrl / siteLogo / siteName в Sprinty; логотип — загрузка файла вместо URL. */
const siteTripleSchema: Schema = {
  siteUrl: {
    required: true,
    label: "URL сайта",
    helperText: "Пример: https://example.com",
    type: "url",
    xs: 12,
    md: 4,
    fullWidth: true,
  },
  logoId: {
    label: "Логотип сайта",
    helperText: "Для шаблонов писем (jpeg, png).",
    type: "image",
    imageType: "image",
    xs: 12,
    md: 4,
    fullWidth: true,
  },
  siteName: {
    required: true,
    label: "Название сайта",
    helperText: "Пример: Ottkit",
    type: "text",
    xs: 12,
    md: 4,
    fullWidth: true,
  },
};

const siteEmailSchema: Schema = {
  siteEmail: {
    required: true,
    label: "Email сайта",
    helperText: "Пример: hello@example.com — для шаблонов и контактов.",
    type: "email",
    xs: 12,
    md: 4,
    fullWidth: true,
  },
};

const mainFieldsSchemaCreate: Schema = {
  ...fromAndTitleSchema,
  ...apiKeyFieldCreate,
  ...siteTripleSchema,
  ...siteEmailSchema,
};

const mainFieldsSchemaEdit: Schema = {
  ...fromAndTitleSchema,
  ...apiKeyFieldEdit,
  ...siteTripleSchema,
  ...siteEmailSchema,
};

export const createSchema: CreateSchemaDefinition = {
  main: {
    label: "Основные настройки",
    createMutation: CreateMailerConfigMutation,
    schema: mainFieldsSchemaCreate,
  },
} as const;

export const editSchemaBase: EditSchemaDefinition = {
  details: {
    label: "Просмотр",
    type: "show" as const,
    getTitle: (data: unknown) => {
      const typed = data as MailerConfigShowQueryType;
      return typed?.mailerConfig?.title ?? null;
    },
    showQuery: MailerConfigShowQuery,
  },
  edit: {
    label: "Основные настройки",
    type: "form" as const,
    getTitle: (data: unknown) => {
      const typed = data as MailerConfigShowQueryType;
      return typed?.mailerConfig?.title ?? null;
    },
    updateMutation: UpdateMailerConfigMutation,
    editQuery: MailerConfigEditQuery,
    schema: mainFieldsSchemaEdit,
    getEditData: (data: unknown): UpdateMailerConfigInput => {
      const { mailerConfig: mc } = data as MailerConfigEditQueryType;
      return {
        title: mc.title,
        from: mc.from,
        siteUrl: mc.siteUrl,
        logoId: mc.logo?.id ?? null,
        siteName: mc.siteName,
        siteEmail: mc.siteEmail,
      };
    },
  },
} as const;

export function createEditSchema(
  showComponent: ComponentType<{ editData?: unknown; id?: string }>
): EditSchemaDefinition {
  return {
    ...editSchemaBase,
    details: {
      ...editSchemaBase.details,
      showComponent,
    },
  };
}
